import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  SUMMARY_FIELDS, COMPOSITION_FIELDS, MUSCLE_FAT_FIELDS, BODY_MASS_FIELDS,
  SEGMENTS, STATUS_TONE, fmtValue, fmtRange, fmtTestTime, reportValue,
} from "./bodyCompFields";

const StatusPill = ({ status, testId }) => {
  if (!status) return <span className="text-[10px] text-muted-foreground">—</span>;
  const tone = STATUS_TONE[status] || STATUS_TONE.Standard;
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide"
      style={{ background: tone.bg, color: tone.fg }}
      data-testid={testId}
    >
      {status}
    </span>
  );
};

const MetricTable = ({ title, fields, report, testId }) => (
  <div data-testid={testId}>
    <p className="text-sm font-semibold mb-2">{title}</p>
    <div className="rounded-xl border overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-muted/60">
          <tr>
            <th className="text-left font-medium px-3 py-2">Item</th>
            <th className="text-right font-medium px-3 py-2">Value</th>
            <th className="text-right font-medium px-3 py-2 w-32">Reference</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.key} className="border-t">
              <td className="px-3 py-2">{f.label}{f.unit ? ` (${f.unit})` : ""}</td>
              <td className="px-3 py-2 text-right font-medium" data-testid={`${testId}-${f.key}`}>
                {fmtValue(reportValue(report, f.key))}
              </td>
              <td className="px-3 py-2 text-right text-muted-foreground">
                {fmtRange(report?.ranges?.[f.key]) || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const SegmentPanel = ({ title, data, testId }) => (
  <div className="rounded-xl border p-4" data-testid={testId}>
    <p className="text-sm font-semibold mb-3">{title}</p>
    <div className="space-y-2">
      {SEGMENTS.map((s) => (
        <div key={s.key} className="flex items-center justify-between gap-2 border-b last:border-0 pb-2 last:pb-0">
          <span className="text-xs text-muted-foreground">{s.label}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium" data-testid={`${testId}-${s.key}-value`}>
              {fmtValue(data?.[s.key]?.value)}
            </span>
            <StatusPill status={data?.[s.key]?.status} testId={`${testId}-${s.key}-status`} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** One saved HOWBODY report, laid out the way the printed sheet reads. */
export const BodyCompReportView = ({ report }) => {
  if (!report) {
    return (
      <Card className="rounded-2xl"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="body-comp-report-empty">
        No report yet — upload your Body Composition Analysis Report to see it here.
      </CardContent></Card>
    );
  }
  const score = reportValue(report, "comprehensive_score");
  const info = report.user_info || {};

  return (
    <div className="space-y-5" data-testid="body-comp-report-view">
      <Card className="rounded-2xl">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <div className="flex items-end gap-3">
              <span className="font-editorial text-4xl leading-none" data-testid="body-comp-report-score">
                {score === null ? "—" : fmtValue(score, 0)}
              </span>
              <span className="text-xs text-muted-foreground pb-1">Comprehensive Score / 100</span>
            </div>
            <div className="text-right text-[11px] text-muted-foreground">
              <p data-testid="body-comp-report-test-time">Test Time: {fmtTestTime(report.test_time)}</p>
              {(info.gender || info.age || info.height_cm) && (
                <p data-testid="body-comp-report-user-info">
                  {[info.gender, info.age ? `${fmtValue(info.age, 0)} yrs` : null,
                    info.height_cm ? `${fmtValue(info.height_cm, 0)} cm` : null]
                    .filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="body-comp-summary-row">
            {SUMMARY_FIELDS.map((f) => (
              <div key={f.key} className="rounded-xl border bg-muted/30 px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground leading-tight">
                  {f.label}{f.unit ? ` (${f.unit})` : ""}
                </p>
                <p className="font-editorial text-xl leading-none mt-1.5" data-testid={`body-comp-summary-${f.key}`}>
                  {fmtValue(reportValue(report, f.key))}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardContent className="p-5 sm:p-6 space-y-5">
          <MetricTable title="Body Composition Analysis" fields={COMPOSITION_FIELDS} report={report} testId="body-comp-table-composition" />
          <MetricTable title="Muscle-Fat Analysis" fields={MUSCLE_FAT_FIELDS} report={report} testId="body-comp-table-musclefat" />
          <MetricTable title="Body Mass Analysis" fields={BODY_MASS_FIELDS} report={report} testId="body-comp-table-bodymass" />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SegmentPanel title="Segmental Fat (kg)" data={report.segmental_fat} testId="body-comp-segmental-fat" />
        <SegmentPanel title="Segmental Muscle (kg)" data={report.segmental_muscle} testId="body-comp-segmental-muscle" />
      </div>
    </div>
  );
};

export default BodyCompReportView;
